import { AnimatePresence, motion } from 'motion/react';
import { FormattedMessage } from 'react-intl';
import { useSessionstorageState } from 'rooks';

import { Icon, Text } from 'components/@tedui';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import useGetBrowserSupportDetails from './useGetBrowserSupportDetails';

import type { JSX } from 'react';

export default function UpdateBrowserBanner(): JSX.Element {
  const supportDetails = useGetBrowserSupportDetails();
  const [isBannerOpen, setIsBannerOpen] = useSessionstorageState(
    'show-browser-update-banner',
    supportDetails !== null
  );

  const isMobile = useIsBreakpointWidth({});

  return (
    <AnimatePresence>
      {supportDetails && isBannerOpen && (
        <motion.section
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          transition={{ ease: 'easeOut', delay: 0.3 }}
          className="relative bg-gray-300"
        >
          <div className="mx-auto flex flex-col px-4 py-10 lg:max-w-screen-lg lg:flex-row">
            <button type="button" onClick={() => setIsBannerOpen(false)}>
              <Icon
                iconName="x"
                className="absolute right-0 top-0 mr-4 mt-2 text-2xl lg:mt-10"
              />
            </button>

            <div>
              <Text tag="h2" variant="header2" isBold={isMobile}>
                <FormattedMessage defaultMessage="Your browser could use a refresh!" />
              </Text>

              <Text
                tag="p"
                variant="caption"
                UNSAFE_className="mt-1 mb-6 text-gray-700"
              >
                <FormattedMessage defaultMessage="Your web browser is out of date. Update your browser for more security, speed and the best experience on this site." />
              </Text>
            </div>

            <div className="mx-auto flex flex-col">
              <div className="mx-auto flex items-center">
                <div className="flex flex-col items-center">
                  <supportDetails.Icon className="mb-2 w-12" />
                  <Text isBold variant="label">
                    {supportDetails.browserName} {supportDetails.browserVersion}
                  </Text>
                </div>

                <Icon
                  iconName="arrow-right-long"
                  className="mx-6 text-5xl text-gray-700"
                />

                <a
                  href={supportDetails.downloadUrl}
                  className="flex flex-col items-center"
                  target="_blank"
                  rel="noreferrer"
                >
                  <supportDetails.Icon className="mb-2 w-12" />
                  <Text isBold variant="label">
                    {supportDetails.browserName}
                  </Text>
                </a>
              </div>

              <div className="my-3 border-b-thin border-gray-500 opacity-50" />

              <Text tag="p" variant="caption">
                <FormattedMessage defaultMessage="For questions contact us at support@ted.com" />
              </Text>
            </div>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
}
